import React, { useState } from 'react';

import './checkbox-filters.styles.scss';

export interface FiltersProps {
  onFilter: (filters: { [key: string]: string[] }) => void;
  status: boolean;
  locations?: string[];
  label: string[];
}

interface FilterParentProps {
  onFilter: (filters: { [key: string]: string[] }) => void;
  onClearFilters?: () => void;
  locations?: string[];
  title?: string;
  checkboxLabels: string[];
  filterKey: string; // Add this to specify which filter type this component controls
}

const CheckboxFilters: React.FC<FilterParentProps> = ({
  title = 'Default Title',
  checkboxLabels = ['Checkbox 1', 'Checkbox 2', 'Checkbox 3', 'Checkbox 4'],
  onFilter,
  filterKey, // This is important - identifies which filter type we're working with
}) => {
  const [checkboxes, setCheckboxes] = useState<string[]>([]);

  // Function to transform education options to match the formats used in job listings
  const transformEducationValues = (educationOptions: string[]): string[] => {
    const formattedItems: string[] = [];

    // Make sure we're using the same education options as in FilterEducation
    educationOptions.forEach((option) => {
      // format for High School/GED
      if (option === 'High School/GED') {
        formattedItems.push(
          'High School',
          'High school',
          'High School Diploma',
          'GED',
          'General Education Diploma',
        );
      }
      // format for Associate's Degree
      else if (option === "Associate's Degree") {
        formattedItems.push(
          'associates',
          'Associates',
          "associate's",
          "Associate's",
          'A.A',
          'A.S',
          '2-year',
          '2 year',
          'Community College',
          'CC',
        );
      }
      // format for Bachelor's Degree
      else if (option === "Bachelor's Degree") {
        formattedItems.push(
          'bachelors',
          'Bachelors',
          "bachelor's",
          "Bachelor's",
          'B.A',
          'B.S',
          '4-year',
          '4 year',
          'Undergraduate',
          'undergraduate',
          'majoring',
          'Freshman',
          'Sophomore',
          'Junior',
          'Senior',
        );
      }
      // format for Masters/PhD Degree
      else if (option === "Master's/PhD Degree") {
        formattedItems.push(
          'Masters',
          "Master's",
          'M.S',
          'MBA',
          'M.B.A',
          'PhD',
          'Ph.D',
          'Doctoral',
          'Doctorate',
        );
      } else {
        // If no mapping is found, just add the original option
        formattedItems.push(option);
      }
    });

    return formattedItems;
  };

  const handleCheckboxChange = (label: string) => {
    // Update checkboxes state
    const updatedCheckboxes = checkboxes.includes(label)
      ? checkboxes.filter((item) => item !== label)
      : [...checkboxes, label];

    setCheckboxes(updatedCheckboxes);

    // Send updated filters to parent component with the proper key
    if (updatedCheckboxes.length === 0) {
      const emptyFilter: { [key: string]: string[] } = {};
      emptyFilter[filterKey] = [];
      onFilter(emptyFilter);
    } else {
      const updatedFilter: { [key: string]: string[] } = {};

      // Apply transformation for education filters
      if (filterKey === 'education') {
        updatedFilter[filterKey] = transformEducationValues(updatedCheckboxes);
      } else {
        updatedFilter[filterKey] = updatedCheckboxes;
      }

      onFilter(updatedFilter);
    }
  };

  const handleClear = () => {
    setCheckboxes([]);
    // Send empty filter for this type
    const emptyFilter: { [key: string]: string[] } = {};
    emptyFilter[filterKey] = [];
    onFilter(emptyFilter);
  };

  return (
    <form onSubmit={(event) => event.preventDefault()}>
      <fieldset>
        <div className="sidebarSearchTitleContainer">
          <legend>{title}</legend>
          <button
            className="sidebarClearButton"
            onClick={(e) => {
              e.preventDefault();
              handleClear();
            }}>
            Clear
          </button>
        </div>

        <div className="mobileCheckboxesContainer">
          {checkboxLabels.map((item, index) => (
            <div key={index}>
              <input
                type="checkbox"
                id={`${filterKey}-checkbox-index-${index}`}
                checked={checkboxes.includes(item)}
                value={item}
                onChange={() => handleCheckboxChange(item)}
                className="mobileSidebarCheckbox"
              />
              <label style={{ marginLeft: '10px' }}>{item}</label>
            </div>
          ))}
        </div>
      </fieldset>
    </form>
  );
};

export default CheckboxFilters;
